import { queryOptions, useQuery } from "@tanstack/react-query";
import { listPublishedTestimonials } from "@/lib/testimonials.functions";
import { Star } from "lucide-react";

export const testimonialsQuery = () =>
  queryOptions({
    queryKey: ["public", "testimonials"],
    queryFn: () => listPublishedTestimonials(),
    staleTime: 5 * 60 * 1000,
  });

const QUOTE_TINTS = ["bg-maroon-100 text-maroon-500", "bg-teal-100 text-teal-700", "bg-amber-100 text-amber-800"];

/** Shared testimonial card (homepage reference): tinted initial, Cairo quote, name — city. */
export function TestimonialCard({ t, i, fullAuthor = false, showRating = false, className = "bg-surface border border-hairline-subtle shadow-sm" }: { t: any; i: number; fullAuthor?: boolean; showRating?: boolean; className?: string }) {
  const first = String(t.author_name ?? "").trim().split(/\s+/)[0] ?? "";
  return (
    <figure className={"h-full rounded-ds-xl p-5 " + className}>
      <blockquote className="font-display text-[17px] font-bold leading-[1.7] text-text-primary lg:text-[19px]">«{t.quote_ar}»</blockquote>
      <figcaption className="mt-4 flex items-center gap-3 text-[14px] text-text-secondary">
        <span aria-hidden className={"grid h-10 w-10 shrink-0 place-items-center rounded-full font-display font-extrabold " + QUOTE_TINTS[i % 3]}>
          {first.charAt(0)}
        </span>
        <span className="min-w-0 flex-1">
          {fullAuthor ? <><span className="block font-display font-extrabold">{t.author_name}</span><span className="mt-0.5 block text-[13px]">{[t.author_role, t.author_city].filter(Boolean).join(" — ")}</span></> : <>{[first, t.author_role].filter(Boolean).join("، ")}{t.author_city ? ` — ${t.author_city}` : ""}</>}
        </span>
        {showRating && t.rating ? (
          <span className="flex shrink-0 items-center gap-0.5 text-amber-800" role="img" aria-label={`${t.rating} من 5`}>
            {Array.from({ length: t.rating }, (_, star) => <Star key={star} className="h-3.5 w-3.5 fill-current" aria-hidden="true" />)}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}

export function TestimonialsStrip({ limit = 6 }: { limit?: number }) {
  const { data, isLoading } = useQuery(testimonialsQuery());
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-ds-xl bg-cream-200" />
        ))}
      </div>
    );
  }
  const rows = (data ?? []).slice(0, limit);
  if (!rows.length) return null;
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {rows.map((t: any, i: number) => <TestimonialCard key={t.id} t={t} i={i} fullAuthor showRating />)}
    </div>
  );
}